import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { FaGoogle } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkAuthStatus } from '../api/auth';
import axios from 'axios';

// Page container style for centering (matching ContactSearch)
const pageContainerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '-7vh' // Move content higher on the page
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false); // Renamed for clarity

  // Memoize handleSuccessfulAuth to ensure it has a stable reference if used in useEffect
  const handleSuccessfulAuth = useCallback(() => {
    console.log("handleSuccessfulAuth called");
    const redirectUrl = localStorage.getItem('redirectUrl');
    localStorage.removeItem('redirectUrl'); 
    let finalRedirectPath = '/'; 

    if (redirectUrl) {
      try {
        const redirectToUri = new URL(redirectUrl, window.location.origin);
        if (redirectToUri.pathname !== '/login') { // Check against '/login' specifically
          finalRedirectPath = redirectToUri.href;
        } else {
          console.warn(`Original redirectUrl ("${redirectUrl}") was the login page. Will redirect to home ("/") instead.`);
        }
      } catch (e) {
        console.error("Invalid redirect URL in localStorage:", redirectUrl, e);
      }
    }
    console.log("Auth success callback: Hard redirecting to:", finalRedirectPath);
    window.location.href = finalRedirectPath; // Perform hard redirect
  }, []);

  useEffect(() => {
    console.log("Login useEffect triggered", { 
      pathname: location.pathname, 
      search: location.search, 
      isProcessingAuth 
    });

    const params = new URLSearchParams(location.search);
    const authSuccess = params.get('auth_success');
    const errorMsg = params.get('error');

    // If already processing, don't do anything else.
    if (isProcessingAuth) {
      console.log("Already processing auth, exiting useEffect.");
      return;
    }

    // Case 1: OAuth callback success
    if (authSuccess === 'true') {
      console.log("Auth success detected in URL (auth_success=true).");
      setIsProcessingAuth(true); // Set flag to prevent other checks
      // Clear the auth_success from URL immediately to prevent re-processing on refresh/back
      window.history.replaceState({}, document.title, location.pathname); 
      handleSuccessfulAuth(); // This will perform a hard redirect
      return; // Exit useEffect
    }

    // Case 2: Error message in URL
    if (errorMsg) {
      console.log("Error message detected in URL:", errorMsg);
      setError(decodeURIComponent(errorMsg));
      // Clear the error from the URL
      window.history.replaceState({}, document.title, location.pathname);
      // No return here, fall through to check auth status as the error might be from a stale attempt
    }

    // Case 3: No OAuth callback, check current authentication status
    // This runs if not an auth_success callback and not already processing.
    console.log("Checking auth status via checkAuthStatus().");
    checkAuthStatus()
      .then(response => {
        if (response.isAuthenticated) {
          console.log("User is already authenticated (from checkAuthStatus). Navigating to home.");
          setIsProcessingAuth(true); // Set flag
          navigate('/'); // Use React Router navigate for client-side navigation
        } else {
          console.log("User is not authenticated (from checkAuthStatus). Staying on login page.");
          // No action needed, user stays on login page.
        }
      })
      .catch(err => {
        console.error('Error checking authentication status:', err);
        setError('Could not verify authentication status. Please try again.');
      });

  }, [location.search, location.pathname, navigate, isProcessingAuth, handleSuccessfulAuth]); // Added dependencies

  const handleGoogleLogin = async () => {
    try {
      setError(null);
      setIsProcessingAuth(true); // Indicate an auth process is starting
      console.log('Fetching Google auth URL...');
      const response = await axios.get('/api/auth/google-url');
      console.log('Redirecting to Google auth:', response.data.url);
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Error redirecting to Google auth:', err);
      setError('Unable to connect to authentication service. Please try again later.');
      setIsProcessingAuth(false); // Reset if the initial step fails
    }
  };

  return (
    <Container style={pageContainerStyle}>
      <Row className="justify-content-center w-100">
        <Col md={6} lg={5}>
          <Card className="shadow-lg border-0">
            <Card.Body className="p-5" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="text-center mb-4">
                <h4 className="mb-3">Welcome</h4>
                <p className="text-muted">Please sign in with your Google account to continue</p>
              </div>
              
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}
              
              <Button 
                variant="outline-primary" 
                size="lg" 
                className="w-100 d-flex align-items-center justify-content-center gap-2 mb-3"
                onClick={handleGoogleLogin}
                disabled={isProcessingAuth} // Disable button while processing
              >
                <FaGoogle />
                <span>Sign in with Google</span>
              </Button>
              
              <div className="mt-4 text-center">
                <small className={error ? "text-danger fw-bold" : "text-muted"}>
                  You must be an authorized user to access this application.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Login;
